import { format, parseISO } from 'date-fns'
import { theme } from '@elphasecure/elpha-design-system'
import moment from 'moment'

// Currency Helpers
export const currencyFormatter = (amount: number) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return formatter.format(amount)
}

// Time Helpers
export const dateLocalizer = (time) => {
  const parseTime = parseISO(time)
  return format(parseTime, 'P')
}

export const timeLocalizer = (time) => {
  const parseTime = parseISO(time)
  return format(parseTime, 'p')
}

const MINUTE_IN_MILLISECONDS = 1000 * 60
const HOUR_IN_MILLISECONDS = MINUTE_IN_MILLISECONDS * 60
const DAY_IN_MILLISECONDS = HOUR_IN_MILLISECONDS * 24

export const timeSince = (dateString: string) => {
  if (!dateString) return ''

  let createdDate = moment.utc(dateString)
  let now = Date.now()
  let diff = now - createdDate.valueOf()

  // If the difference is less than 1 minute, return x seconds ago
  if (diff < MINUTE_IN_MILLISECONDS) {
    let seconds = Math.floor(diff / 1000)
    if (seconds === 0) {
      return `Just now`
    }
    return `${seconds} second${seconds > 1 ? 's' : ''} ago`
  } else if (diff < HOUR_IN_MILLISECONDS) {
    let minutes = Math.floor(diff / MINUTE_IN_MILLISECONDS)
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
  } else if (diff < DAY_IN_MILLISECONDS) {
    let hours = Math.floor(diff / HOUR_IN_MILLISECONDS)
    return `${hours} hour${hours > 1 ? 's' : ''} ago`
  } else {
    // shows local
    let dateString = moment(createdDate).local().format('MMM D, YYYY h:mm A')
    return dateString
  }
}

// Notication Helpers
export const notifications = (issue) => {
  switch (issue.message.type) {
    case 'adenc_alert1':
      return {
        message: `We've detected an anomaly that needs your attention.`,
        details: `Your device may be infected. Please contact our experts for assistance.`,
      }
    case 'pvm_alert1':
      return {
        message: `${issue.message.program} is out of date.`,
        details: `You have version ${issue.messageDetails.installed_version} installed. Update to version ${issue.messageDetails.available_version}.`,
      }
    case 'cls_url_phishing_alert1':
      return {
        message: `A suspicious website was visited.`,
        details: `Website ${issue.messageDetails.domain} was accessed from the device and may be a phishing website. Change your passwords on any legitimate websites with a similar name and on any websites your were directed to from the above link.`,
      }
    default:
      return {
        message: `${issue.message}`,
        details: `${issue.messageDetails}`,
      }
  }
}

export const quoteStatus = (status: string) => {
  switch (status) {
    case 'accepted':
      return 'Quoted'
    default:
      return status.charAt(0).toUpperCase() + status.slice(1)
  }
}

export const statusColors = (status: string) => {
  status = status.toLowerCase()
  switch (status) {
    case 'success':
    case 'submitted':
    case 'finished':
    case 'ready':
    case 'draft':
      return theme.palette.success.main
    case 'info':
    case 'low':
    case 'low_risk':
    case 'message':
    case 'unknown':
    case 'running':
    case 'quoted':
    case 'accepted':
      return theme.palette.info.main
    case 'elevated_risk':
    case 'elevated':
      return theme.palette.alert.main
    case 'alert':
    case 'medium':
    case 'warning':
    case 'high':
    case 'high_risk':
    case 'underwriting':
    case 'pending':
      return theme.palette.warning.main
    case 'danger':
    case 'critical':
    case 'failed':
    case 'rejected':
    case 'voided':
    case 'canceled':
      return theme.palette.error.main
    case 'processing':
    case 'expired':
      return theme.palette.primary[200]
    case 'bound':
    case 'issued':
    case 'informational':
      return theme.palette.secondary.main
    case 'incomplete':
      return theme.palette.common[500]
    default:
      return theme.palette.primary[200]
  }
}

export const bytesToSize = (bytes) => {
  var sizes = ['bytes', 'kb', 'mb', 'gb', 'tb']
  if (bytes === 0) return '0 Byte'
  var i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`
}

export const stringToHHMMSS = (valueToConvert: number) => {
  let secNum = valueToConvert
  let hours = Math.floor(secNum / 3600)
  let minutes = Math.floor((secNum - hours * 3600) / 60)
  if (minutes < 1) return `1m`
  return `${hours}h ${minutes}m`
}

export const truncateString = (str: string, length: number) => {
  if (str.length <= length) return str
  return str.substring(0, length - 1) + '...'
}
